import { z } from 'zod';
import { cognitoKindSchema } from '../auth';

/**
 * A `user` is an authenticated person backed by an AWS Cognito user.
 * The Cognito-derived fields (`isEnabled`, `sub`, `username`, `email`,
 * `phoneNumber`) are read-only snapshots synced from Cognito when the user is
 * first created.
 *
 * Which locations a user may access is stored separately, in `userLocations`.
 */
export const userSchema = z.object({
  name: z.string(),

  //
  // Derived from Cognito user attributes
  //
  isEnabled: z.boolean(),
  sub: z.string().describe('User identity `subject` or AWS Cognito `sub` user attribute'),
  /**
   * Read only. Derived from the Cognito `Username`. This pool signs users in by
   * email alias, so Cognito assigns an opaque uuid as the `Username` — it is NOT
   * the email. Show `email` to people, not this.
   */
  username: z.string(),
  /**
   * Read only. Derived from the Cognito `email` attribute — the address the user
   * signs in with, and the only human-readable identifier we store (`username` is
   * a uuid). Optional: rows created before this field existed have no email, and
   * Cognito remains the authority when resolving an email to an account.
   */
  email: z.string().optional(),
  /**
   * `phoneNumber` is read only. Derived from Cognito user 'phone_number' attribute.
   * Optional — users created by email invite have no phone number.
   * In international format, E.g. +61466308221"
   */
  phoneNumber: z.string().optional(),
  /**
   * Read only. Mirror of the Cognito `custom:kind` attribute, synced when the user
   * is created; absence ⇒ standard user. Display-only — authorization always reads
   * the token claim (`assertIsSuperAdminX`), never this field.
   */
  kind: cognitoKindSchema.optional(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
  updatedAt: z.number().optional(),
});
export type User = z.output<typeof userSchema>;

export const userStrictSchema = userSchema.safeExtend({
  name: z.string().trim().min(1),
});

export const userCvxSchema = userSchema.safeExtend({
  // Derived from Cognito user attributes (required once persisted)
  isEnabled: z.boolean(),
  sub: z.string(),
  username: z.string(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  kind: cognitoKindSchema.optional(),

  _creationTime: z.number(),
  _id: z.string(),
  updatedAt: z.number(),
});
export type UserCvx = z.output<typeof userCvxSchema>;
